import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts'
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  Zap,
  Car,
  AlertTriangle,
  Landmark,
  TrendingDown,
  Factory,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react'
import { calculateLeasing, MAX_RATE_LCC, tuneLeaseComparisonColumns } from '../utils/leasingEngine'
import {
  applyCurveToResidualEntries,
  parseJsonBodySafe,
  slopesFromPrefsFallback,
} from '../utils/residualCurve'
import { LEASING_HISTORY_MAX, loadLeasingHistory, persistLeasingHistory } from '../utils/leasingHistory'
import { fetchWithTimeout } from '../utils/fetchWithTimeout'
import { BACKEND_URL as BACKEND } from '../config/backendUrl'
const DURATION_OPTIONS = [24, 36, 48, 60]
const KM_OPTIONS = [5000, 10000, 15000, 20000, 30000, 40000, 60000]
const AUTO_MARQUES = new Set(['audi', 'porsche'])
/** Canton pour l’analyse risque IA ; TODO: brancher user.canton depuis le profil quand disponible. */
const LEASING_RISK_CANTON_DEFAULT = 'GE'

function formatCarrosserieLabel(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  return raw.toLowerCase() === 'estate' ? 'break' : raw
}

function formatChf(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return `${Math.round(n).toLocaleString('fr-CH')} CHF`
}

function formatTaegPct(taeg) {
  const n = Number(taeg)
  if (!Number.isFinite(n) || n < 0 || n > 99) return '—'
  const s = n.toLocaleString('fr-CH', { maximumFractionDigits: 2, minimumFractionDigits: 1 })
  return `${s} %`
}

function confidenceFr(residual) {
  const c = String(residual?.confidence || '').toLowerCase()
  if (c === 'haute' || c === 'high')
    return { txt: 'Haute', color: '#86efac', tip: 'Base marché robuste (annonces ou ventes suffisantes).' }
  if (c === 'moyenne' || c === 'medium')
    return { txt: 'Moyenne', color: '#fde68a', tip: 'Signal marché exploitable mais partiel.' }
  return { txt: 'Faible', color: '#fca5a5', tip: 'Données limitées sur ce modèle — prudence sur la résiduelle.' }
}

function liquidityLabelFr(raw) {
  const s = String(raw || '').trim()
  if (!s) return null
  const m = {
    'good liquidity': 'Bonne liquidité',
    'medium liquidity': 'Liquidité moyenne',
    'low liquidity': 'Faible liquidité',
    Good: 'Bonne liquidité',
    Medium: 'Moyenne',
    Low: 'Faible',
  }
  return m[s] || m[s.toLowerCase()] || s
}

function residualMethodText(residual, modelLabel) {
  const m = String(residual?.method || '')
  const nV = Number(residual?.realSalesSampleSize) || 0
  const nA = Number(residual?.marketSampleSize) || 0
  const ml = String(modelLabel || 'ce modèle').trim().toUpperCase()
  if (m === 'proxy_ventes_reelles' && nV > 0) {
    return `Basé sur ${nV} vente${nV > 1 ? 's' : ''} réelle${nV > 1 ? 's' : ''} marché pour des ${ml} équivalents (âge/km projetés).`
  }
  if (m === 'proxy_cotation' && nA > 0) {
    return `Basé sur ${nA} annonce${nA > 1 ? 's' : ''} marché (proxy cotation) pour des ${ml} équivalents.`
  }
  if (m === 'slope_extrapolation') {
    return 'Extrapolation à partir des pentes de dépréciation (données marché partielles).'
  }
  return 'Méthode interne RadarAuto (détail non disponible).'
}

function residualMethodLabel(method) {
  const m = String(method || '').trim()
  if (m === 'proxy_ventes_reelles') return 'Ventes réelles comparables'
  if (m === 'proxy_cotation') return 'Proxy cotation marché'
  if (m === 'slope_extrapolation') return 'Extrapolation par pentes'
  return m || 'Interne'
}

function sortModelesAlphabetiques(list) {
  const rows = Array.isArray(list) ? [...list] : []
  rows.sort((a, b) =>
    String(a?.model || '').localeCompare(String(b?.model || ''), 'fr', {
      sensitivity: 'base',
      numeric: true,
    })
  )
  return rows
}

function confidenceRiskIaFr(c) {
  const k = String(c || '').toLowerCase()
  if (k === 'high')
    return { txt: 'Haute', color: '#86efac', tip: 'Synthèse appuyée par plusieurs signaux vérifiables.' }
  if (k === 'medium')
    return { txt: 'Moyenne', color: '#fde68a', tip: 'Interprétation partielle — croiser avec votre contexte.' }
  return { txt: 'Faible', color: '#fca5a5', tip: 'Signaux limités ou analyse indisponible.' }
}

function riskIaEventDedupeKey(r) {
  const cat = String(r?.category || '').toLowerCase().trim()
  const ev = String(r?.event || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\d]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160)
  return `${cat}|${ev}`
}

function dedupeRisksDisplay(risks) {
  const seen = new Set()
  const out = []
  for (const r of risks || []) {
    const ev = String(r?.event || '').trim()
    if (!ev) {
      out.push(r)
      continue
    }
    const k = riskIaEventDedupeKey(r)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(r)
  }
  return out
}

function normalizeRiskIaImpact(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return n > 0 ? -n : n
}

function riskIaProbabilityWeight(probability) {
  const p = String(probability || '').toLowerCase()
  if (p === 'high') return 1
  if (p === 'medium') return 0.5
  return 0.2
}

function normalizeRiskAiPayload(payload, baseResidualValue) {
  const raw = payload && typeof payload === 'object' ? payload : {}
  const risks = (Array.isArray(raw.risks) ? raw.risks : []).map((r) => ({
    ...r,
    impact_pct: normalizeRiskIaImpact(r?.impact_pct),
  }))
  const weightedFromRisks = risks.reduce(
    (sum, r) => sum + normalizeRiskIaImpact(r?.impact_pct) * riskIaProbabilityWeight(r?.probability),
    0
  )
  const totalFromServer = normalizeRiskIaImpact(raw.total_impact_pct)
  const total_impact_pct = Number.isFinite(totalFromServer)
    ? totalFromServer
    : risks.length
      ? Math.round(weightedFromRisks * 1000) / 1000
      : 0
  const base = Number(baseResidualValue)
  const adjustedResidualValue =
    Number.isFinite(base) && base > 0 && Number.isFinite(total_impact_pct)
      ? base * (1 + total_impact_pct / 100)
      : Number.isFinite(Number(raw.adjustedResidualValue))
        ? Number(raw.adjustedResidualValue)
        : 0
  return {
    ...raw,
    risks,
    total_impact_pct,
    adjustedResidualValue: Number.isFinite(adjustedResidualValue) ? adjustedResidualValue : 0,
    guard_applied: raw.guard_applied != null ? raw.guard_applied : undefined,
    guard_note: raw.guard_note != null && String(raw.guard_note).trim() ? String(raw.guard_note).trim() : undefined,
  }
}

function riskIaCategoryIcon(category) {
  const c = String(category || '').toLowerCase()
  const size = 16
  const common = { size, strokeWidth: 2, 'aria-hidden': true }
  switch (c) {
    case 'regulation':
      return <Shield {...common} />
    case 'successor':
      return <Car {...common} />
    case 'emissions':
      return <Factory {...common} />
    case 'brand':
      return <AlertTriangle {...common} />
    case 'tech':
      return <Zap {...common} />
    case 'fiscal':
      return <Landmark {...common} />
    case 'market':
    default:
      return <TrendingDown {...common} />
  }
}

function probabilityChipIa(p) {
  const k = String(p || '').toLowerCase()
  const map = {
    low: { txt: 'Faible', bg: 'rgba(148,163,184,0.15)', fg: '#94a3b8' },
    medium: { txt: 'Moyenne', bg: 'rgba(251,191,36,0.12)', fg: '#fcd34d' },
    high: { txt: 'Élevée', bg: 'rgba(248,113,113,0.12)', fg: '#fca5a5' },
  }
  const x = map[k] || map.low
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        background: x.bg,
        color: x.fg,
      }}
    >
      {x.txt}
    </span>
  )
}

export function LeasingPage({ token, user = null, prefs, prefillRequest = null, onPrefillHandled = null }) {
  const [marques, setMarques] = useState([])
  const [modeles, setModeles] = useState([])
  const [annees, setAnnees] = useState([])
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [annee, setAnnee] = useState('')
  const [km, setKm] = useState('')
  const [version, setVersion] = useState('')
  const [autoPrecisionEnabled, setAutoPrecisionEnabled] = useState(false)
  const [cotationCarrosseries, setCotationCarrosseries] = useState([])
  const [cotationMotorisations, setCotationMotorisations] = useState([])
  const [carrosserieChoisie, setCarrosserieChoisie] = useState('')
  const [motorisationChoisie, setMotorisationChoisie] = useState('')
  const [estimatedVehiclePrice, setEstimatedVehiclePrice] = useState(null)
  const [garantie, setGarantie] = useState(false)
  const [accident, setAccident] = useState(false)
  const [downPct, setDownPct] = useState(20)
  const [downChf, setDownChf] = useState(0)
  const [durationMonths, setDurationMonths] = useState(36)
  const [annualKm, setAnnualKm] = useState(10000)
  const [nominalRate, setNominalRate] = useState(String(Number(prefs?.leasing_taux_defaut ?? 4.9)))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [residual, setResidual] = useState(null)
  const [comparisonCols, setComparisonCols] = useState([])
  const [priceSourceTag, setPriceSourceTag] = useState('cotation')
  const [leasingHistoryOpen, setLeasingHistoryOpen] = useState(false)
  const [leasingHistory, setLeasingHistory] = useState(() => loadLeasingHistory())
  const [riskAiEnabled, setRiskAiEnabled] = useState(() => prefs?.leasing_risk_ai_enabled ?? true)
  /** Résultat calculateLeasing recalculé avec la VR ajustée par l'analyse IA — jamais null si l'analyse réussit. */
  const [riskAdjustedResult, setRiskAdjustedResult] = useState(null)
  const [riskAiLoading, setRiskAiLoading] = useState(false)
  const [riskAiData, setRiskAiData] = useState(null)
  const [riskIaDetailsOpen, setRiskIaDetailsOpen] = useState(false)
  const [riskIaVersion, setRiskIaVersion] = useState(0)
  const riskIaForceRef = useRef(false)
  /** Figés au dernier « Calculer le leasing » : l’appel IA ne se relance pas quand on change seulement la durée. */
  const [riskIaMarketRvAnchor, setRiskIaMarketRvAnchor] = useState(null)
  const [riskIaFbLow, setRiskIaFbLow] = useState(null)
  const [riskIaFbHigh, setRiskIaFbHigh] = useState(null)
  const [showBackTop, setShowBackTop] = useState(false)
  const [isMobileLayout, setIsMobileLayout] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 900 : false
  )
  const leasingScrollRef = useRef(null)
  const stockLinkRef = useRef({ vehicleKey: null, fingerprint: null })
  /** Dernière identité véhicule+VR pour laquelle on a lancé l’IA — évite d’effacer l’affichage au simple « Recalculer ». */
  const riskIaLastIdentityRef = useRef(null)
  const autoOnlyAccount = String(user?.vehicle_scope || '').toLowerCase() === 'auto'
  /** Même logique que la cotation : marques du profil ; si vide, on garde la liste API (évite select vide). */
  const profileMarquesAllowed = useMemo(() => {
    const list = Array.isArray(user?.marques_interet)
      ? user.marques_interet.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean)
      : []
    return [...new Set(list)]
  }, [user?.marques_interet])
  /** Évite que les effets make/model effacent modèle / année lors d’un préremplissage ou d’un clic historique. */
  const leaseHydrateRef = useRef(0)

  useEffect(() => {
    const p = Number(prefs?.leasing_taux_defaut)
    if (Number.isFinite(p) && p > 0) setNominalRate(String(p))
    const d = Number(prefs?.leasing_duree_defaut_mois)
    if ([24, 36, 48, 60].includes(d)) setDurationMonths(d)
    const kmDef = Number(prefs?.leasing_km_annuels_defaut)
    if (Number.isFinite(kmDef) && kmDef > 0) setAnnualKm(kmDef)
    const ac = Number(prefs?.leasing_acompte_defaut_pct)
    if (Number.isFinite(ac) && ac >= 0 && ac <= 40) setDownPct(ac)
    setRiskAiEnabled(prefs?.leasing_risk_ai_enabled ?? true)
  }, [prefs])

  useEffect(() => {
    const updateViewport = () => setIsMobileLayout(window.innerWidth <= 900)
    updateViewport()
    window.addEventListener('resize', updateViewport)
    return () => window.removeEventListener('resize', updateViewport)
  }, [])

  useEffect(() => {
    if (!token) return
    const marquesUrl = autoOnlyAccount ? `${BACKEND}/api/marques?vehicle_type=auto` : `${BACKEND}/api/marques`
    fetchWithTimeout(marquesUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status))
        return r.json()
      })
      .then((data) => {
        const all = Array.isArray(data) ? data : []
        const byType = autoOnlyAccount
          ? all.filter((m) => AUTO_MARQUES.has(String(m?.make || '').toLowerCase()))
          : all
        const filtered =
          profileMarquesAllowed.length > 0
            ? byType.filter((m) => profileMarquesAllowed.includes(String(m?.make || '').toLowerCase()))
            : byType
        setMarques(filtered)
      })
      .catch(() => setMarques([]))
  }, [token, autoOnlyAccount, profileMarquesAllowed])

  useEffect(() => {
    if (!Array.isArray(marques) || marques.length === 0) return
    const current = String(make || '').toLowerCase()
    if (!current) return
    const keys = marques.map((m) => String(m?.make || '').toLowerCase()).filter(Boolean)
    if (keys.includes(current)) return
    setMake('')
  }, [marques, make])

  useEffect(() => {
    if (!make) {
      setModeles([])
      setModel('')
      return
    }
    let skipClear = false
    if (leaseHydrateRef.current > 0) {
      leaseHydrateRef.current -= 1
      skipClear = true
    }
    fetchWithTimeout(`${BACKEND}/api/modeles?make=${encodeURIComponent(make)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setModeles(sortModelesAlphabetiques(data)))
      .catch(() => {})
    if (!skipClear) {
      setModel('')
      setAnnee('')
    }
    setResult(null)
    setResidual(null)
    setEstimatedVehiclePrice(null)
    if (!skipClear) setDownChf(0)
  }, [make, token])

  useEffect(() => {
    if (!make || !model) {
      setAnnees([])
      setAnnee('')
      return
    }
    let skipClear = false
    if (leaseHydrateRef.current > 0) {
      leaseHydrateRef.current -= 1
      skipClear = true
    }
    fetchWithTimeout(
      `${BACKEND}/api/annees?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((data) => setAnnees(Array.isArray(data) ? data : []))
      .catch(() => {})
    if (!skipClear) setAnnee('')
    setResult(null)
    setResidual(null)
    setEstimatedVehiclePrice(null)
    if (!skipClear) setDownChf(0)
  }, [make, model, token])

  useEffect(() => {
    setCarrosserieChoisie('')
    setMotorisationChoisie('')
  }, [make, model, annee])

  useEffect(() => {
    if (!make || !model || !annee || !token) {
      setAutoPrecisionEnabled(false)
      setCotationCarrosseries([])
      setCotationMotorisations([])
      return
    }
    const autoMake = AUTO_MARQUES.has(String(make || '').toLowerCase())
    if (!autoMake && !autoOnlyAccount) {
      setAutoPrecisionEnabled(false)
      setCotationCarrosseries([])
      setCotationMotorisations([])
      return
    }
    const qs = new URLSearchParams({
      make: String(make),
      model: String(model),
      annee: String(annee),
    })
    if (String(motorisationChoisie || '').trim()) qs.set('selected_motorisation', String(motorisationChoisie).trim())
    if (String(version || '').trim()) qs.set('details_precision', String(version).trim())
    fetchWithTimeout(`${BACKEND}/api/cotation-variantes?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const carrosseries = Array.isArray(d?.carrosseries) ? d.carrosseries : []
        const motorisations = Array.isArray(d?.motorisations) ? d.motorisations : []
        setAutoPrecisionEnabled(true)
        setCotationCarrosseries(carrosseries)
        setCotationMotorisations(motorisations)
      })
      .catch(() => {
        setAutoPrecisionEnabled(autoMake || autoOnlyAccount)
        setCotationCarrosseries([])
        setCotationMotorisations([])
      })
  }, [make, model, annee, token, autoOnlyAccount, motorisationChoisie, version])

  /** Nouvelle année modèle → invalider cotation / acompte (évite CHF calés sur l’ancien véhicule). */
  useEffect(() => {
    if (!annee) return
    if (leaseHydrateRef.current > 0) return
    setEstimatedVehiclePrice(null)
    setResult(null)
    setResidual(null)
    setDownChf(0)
    setComparisonCols([])
  }, [annee])

  useEffect(() => {
    if (!prefillRequest) return
    leaseHydrateRef.current = 2
    const makePrefill = String(prefillRequest.make || '').trim().toLowerCase()
    const modelPrefill = String(prefillRequest.model || '').trim()
    const anneePrefill = Number(prefillRequest.annee)
    const kmPrefill = Number(prefillRequest.km)
    const versionPrefill = String(prefillRequest.version || prefillRequest.detailsMoto || '').trim()
    if (makePrefill) setMake(makePrefill)
    if (modelPrefill) setModel(modelPrefill)
    if (Number.isFinite(anneePrefill)) setAnnee(String(anneePrefill))
    if (Number.isFinite(kmPrefill)) setKm(String(Math.round(kmPrefill)))
    setVersion(versionPrefill)
    const vk =
      prefillRequest.id_source != null && prefillRequest.id_source !== ''
        ? String(prefillRequest.id_source)
        : null
    stockLinkRef.current = {
      vehicleKey: vk,
      fingerprint:
        makePrefill && modelPrefill && Number.isFinite(anneePrefill) && Number.isFinite(kmPrefill)
          ? { make: makePrefill, model: modelPrefill, annee: anneePrefill, km: kmPrefill }
          : null,
    }
    setEstimatedVehiclePrice(null)
    setDownChf(0)
    setResult(null)
    setResidual(null)
    onPrefillHandled?.()
    window.setTimeout(() => {
      leaseHydrateRef.current = 0
    }, 0)
  }, [prefillRequest, onPrefillHandled])

  const appendLeasingHistory = useCallback(
    (entry) => {
      setLeasingHistory((prev) => {
        const next = [entry, ...prev].slice(0, LEASING_HISTORY_MAX)
        persistLeasingHistory(next)
        return next
      })
    },
    []
  )

  const currentVehiclePrice = useMemo(() => Number(estimatedVehiclePrice) || 0, [estimatedVehiclePrice])

  const riskAiResidualRv = useMemo(() => {
    if (!residual || residual.error) return null
    const v = Number(residual.residualValue)
    return Number.isFinite(v) && v > 0 ? v : null
  }, [residual])

  const riskAiFbLoForApi = useMemo(() => {
    if (Number.isFinite(riskIaFbLow)) return riskIaFbLow
    if (!residual || residual.error) return null
    const v = Number(residual.fourchetteBasse)
    return Number.isFinite(v) ? v : null
  }, [riskIaFbLow, residual])

  const riskAiFbHiForApi = useMemo(() => {
    if (Number.isFinite(riskIaFbHigh)) return riskIaFbHigh
    if (!residual || residual.error) return null
    const v = Number(residual.fourchetteHaute)
    return Number.isFinite(v) ? v : null
  }, [riskIaFbHigh, residual])

  const riskIaIdentityKey = useMemo(
    () =>
      [
        String(make || '').toLowerCase(),
        String(model || '').trim(),
        String(annee ?? ''),
        String(version || '').trim(),
        String(motorisationChoisie || '').trim(),
        String(carrosserieChoisie || '').trim(),
        riskIaMarketRvAnchor ?? '',
        riskAiResidualRv ?? '',
        riskAiFbLoForApi ?? '',
        riskAiFbHiForApi ?? '',
      ].join('\u0001'),
    [
      make,
      model,
      annee,
      version,
      motorisationChoisie,
      carrosserieChoisie,
      riskIaMarketRvAnchor,
      riskAiResidualRv,
      riskAiFbLoForApi,
      riskAiFbHiForApi,
    ]
  )

  /** VR ajustée affichée : même % IA que l’analyse, appliqué à la résiduelle du contrat pour la durée sélectionnée. */
  const riskIaAdjustedResidualChf = useMemo(() => {
    if (!riskAiData || riskAiData.error) return null
    const base = Number(residual?.residualValue)
    const pct = Number(riskAiData.total_impact_pct)
    if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(pct)) return null
    return base * (1 + pct / 100)
  }, [riskAiData, residual?.residualValue])

  useEffect(() => {
    if (!Number.isFinite(currentVehiclePrice) || currentVehiclePrice <= 0) return
    const capped = Math.max(0, Math.min(40, downPct))
    setDownChf(Math.round((currentVehiclePrice * capped) / 100))
  }, [currentVehiclePrice, downPct])

  const rateNum = Number(nominalRate)
  const lccCap = Number(prefs?.leasing_plafond_lcc_pct)
  const lccLimit = Number.isFinite(lccCap) && lccCap > 0 ? lccCap : MAX_RATE_LCC
  const rateOverLcc = Number.isFinite(rateNum) && rateNum > lccLimit

  /** % IA stable (ancré au calcul) — recalcul local des cartes comparateur sans refetch. */
  const riskIaPctForCompare = useMemo(() => {
    if (!riskAiEnabled || !riskAiData || riskAiData.error) return null
    const pct = Number(riskAiData.total_impact_pct)
    return Number.isFinite(pct) ? pct : null
  }, [riskAiEnabled, riskAiData])

  /** Mensualité / VR par colonne avec le même choc IA que l’affichage principal (cohérent avec le graphique). */
  const comparisonLeaseIaOverlay = useMemo(() => {
    if (riskIaPctForCompare == null || comparisonCols.length === 0 || !Number.isFinite(currentVehiclePrice) || currentVehiclePrice <= 0) {
      return null
    }
    const depositEff = Math.min(Number(downChf) || 0, currentVehiclePrice * 0.4)
    if (depositEff >= currentVehiclePrice) return null
    const rate = Number(nominalRate)
    if (!Number.isFinite(rate) || rate < 0 || rate > MAX_RATE_LCC) return null
    const km = Number(annualKm) || 10000
    const adjustedCols = []
    for (const col of comparisonCols) {
      if (col.error || !col.residual || !col.lease) continue
      const baseRv = Number(col.residual.residualValue)
      if (!Number.isFinite(baseRv) || baseRv <= 0) continue
      let adjRv = baseRv * (1 + riskIaPctForCompare / 100)
      if (!Number.isFinite(adjRv) || adjRv <= 0) continue
      adjRv = Math.floor(adjRv)
      if (adjRv >= currentVehiclePrice) adjRv = Math.max(1, Math.floor(currentVehiclePrice) - 1)
      try {
        const leaseAdj = calculateLeasing({
          vehiclePrice: currentVehiclePrice,
          downPayment: depositEff,
          durationMonths: col.durationMonths,
          annualKm: km,
          nominalRate: rate,
          residualValue: adjRv,
          insuranceMonthly: 0,
        })
        adjustedCols.push({
          ...col,
          residual: { ...col.residual, residualValue: adjRv },
          lease: leaseAdj,
        })
      } catch {
        /* ignore invalid overlay for this column */
      }
    }
    const tunedCols = tuneLeaseComparisonColumns(adjustedCols, {
      vehiclePrice: currentVehiclePrice,
      downPayment: depositEff,
      nominalRate: rate,
      annualKm: km,
    })
    const map = new Map()
    for (const col of tunedCols) {
      if (col.error || !col.residual || !col.lease) continue
      map.set(col.durationMonths, {
        lease: col.lease,
        residualValue: Number(col.residual.residualValue),
        tuningFlags: Array.isArray(col.residual.tuningFlags) ? col.residual.tuningFlags : [],
      })
    }
    return map.size > 0 ? map : null
  }, [comparisonCols, riskIaPctForCompare, currentVehiclePrice, downChf, nominalRate, annualKm])

  const activeComparisonColumn = useMemo(() => {
    const months = Number(durationMonths)
    return comparisonCols.find((col) => Number(col.durationMonths) === months && !col.error) || null
  }, [comparisonCols, durationMonths])

  const activeComparisonOverlay = useMemo(() => {
    const months = Number(durationMonths)
    return comparisonLeaseIaOverlay?.get(months) || null
  }, [comparisonLeaseIaOverlay, durationMonths])

  /** Mensualité / échéancier / détail : même source que la carte active du comparateur. */
  const leaseForDisplay = useMemo(() => {
    if (activeComparisonOverlay?.lease) return activeComparisonOverlay.lease
    if (activeComparisonColumn?.lease) return activeComparisonColumn.lease
    if (!result) return null
    if (riskAiEnabled && riskAdjustedResult) return riskAdjustedResult
    return result
  }, [activeComparisonOverlay, activeComparisonColumn, result, riskAiEnabled, riskAdjustedResult])

  const residualValueForDisplay = useMemo(() => {
    const overlayRv = Number(activeComparisonOverlay?.residualValue)
    if (Number.isFinite(overlayRv) && overlayRv > 0) return overlayRv
    const colRv = Number(activeComparisonColumn?.residual?.residualValue)
    if (Number.isFinite(colRv) && colRv > 0) return colRv
    const riskRv = Number(riskAdjustedResult?.adjustedResidualValue)
    if (riskAiEnabled && Number.isFinite(riskRv) && riskRv > 0) return riskRv
    return Number(residual?.residualValue)
  }, [activeComparisonOverlay, activeComparisonColumn, riskAdjustedResult, riskAiEnabled, residual?.residualValue])

  const displayUsesRiskAdjusted = Boolean(activeComparisonOverlay?.lease) || (!activeComparisonColumn?.lease && riskAiEnabled && Boolean(riskAdjustedResult))

  /** Ligne « Résiduelle » du graphique = dernière valeur de la courbe (cohérent avec l’échéancier affiché). */
  const chartResidualReferenceY = useMemo(() => {
    const n = leaseForDisplay?.breakdown?.length
    if (n > 0) {
      const last = Number(leaseForDisplay.breakdown[n - 1]?.remaining)
      if (Number.isFinite(last)) return last
    }
    return residualValueForDisplay
  }, [leaseForDisplay, residualValueForDisplay])

  const chartData = useMemo(() => {
    if (!leaseForDisplay?.breakdown) return []
    let cumulInterets = 0
    return leaseForDisplay.breakdown.map((b) => {
      cumulInterets += Number(b.interest || 0)
      return {
        month: b.month,
        remaining: Number(b.remaining || 0),
        cumulInterets: Math.round(cumulInterets),
      }
    })
  }, [leaseForDisplay])

  const handleDownChfInput = (v) => {
    const n = Math.max(0, Number(v) || 0)
    setDownChf(n)
    if (currentVehiclePrice > 0) setDownPct(Math.max(0, Math.min(40, (n / currentVehiclePrice) * 100)))
  }

  const onLeasingScroll = useCallback((e) => {
    setShowBackTop(e.currentTarget.scrollTop > 420)
  }, [])

  const scrollLeasingTop = useCallback(() => {
    leasingScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const selectStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: 10,
    background: 'rgba(23,26,31,0.95)',
    border: '1px solid var(--border)',
    color: '#f1f5f9',
    fontFamily: 'inherit',
    fontSize: 14,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    outline: 'none',
    colorScheme: 'dark',
  }
  const optionStyle = { background: '#151920', color: '#e2e8f0' }

  const runCalculation = async () => {
    setError('')
    setComparisonCols([])
    riskIaLastIdentityRef.current = null
    setRiskAiData(null)
    setRiskAdjustedResult(null)
    setRiskIaDetailsOpen(false)
    setLoading(true)
    // On verrouille tout de suite l’état « IA en cours » si elle est activée :
    // évite un flash du bloc résultat entre setResult() et le useEffect IA
    // qui passera ensuite riskAiLoading à true puis (à la fin) à false.
    if (riskAiEnabled) setRiskAiLoading(true)
    try {
      const makeNorm = String(make || '').trim().toLowerCase()
      const modelNorm = String(model || '').trim()
      const anneeNum = Number(annee)
      const kmNum = Number(km)
      const annualKmNum = Number(annualKm)
      const durationNum = Number(durationMonths)
      const nominalRateNum = Number(nominalRate)

      if (!Number.isFinite(anneeNum) || !Number.isFinite(kmNum) || !Number.isFinite(annualKmNum) || !Number.isFinite(durationNum) || !Number.isFinite(nominalRateNum)) {
        throw new Error('Paramètres invalides')
      }
      if (annualKmNum < 1000 || annualKmNum > 200000) {
        throw new Error('Les km annuels doivent être compris entre 1 000 et 200 000')
      }

      if (!makeNorm || !modelNorm) throw new Error('Sélectionnez une marque et un modèle')
      const versionTrimLease = String(version || '').trim()
      /** Moto : le moteur de cotation croise titre + version ; envoyer seulement le modèle masquait la finition (ex. SP). */
      const titrePourMarcheLease =
        autoOnlyAccount || autoPrecisionEnabled
          ? modelNorm
          : [modelNorm, versionTrimLease].filter(Boolean).join(' ').trim() || modelNorm
      const coterRes = await fetchWithTimeout(`${BACKEND}/api/coter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          make: makeNorm,
          model: modelNorm,
          annee: anneeNum,
          km: kmNum,
          ...(autoOnlyAccount ? { vehicle_type: 'auto' } : {}),
          ...(autoPrecisionEnabled ? { vehicle_type: 'auto' } : {}),
          ...(autoPrecisionEnabled && String(motorisationChoisie || '').trim()
            ? { selected_motorisation: String(motorisationChoisie).trim() }
            : {}),
          ...(autoPrecisionEnabled && String(carrosserieChoisie || '').trim()
            ? { selected_carrosserie: String(carrosserieChoisie).trim() }
            : {}),
          ...(autoPrecisionEnabled && String(version || '').trim()
            ? { details_precision: String(version).trim().slice(0, 200) }
            : {}),
          version_listing: version,
          titre_listing: titrePourMarcheLease,
          garantie,
          had_accident: accident,
        }),
      }, 120000)
      const coterRawText = await coterRes.text()
      let coterPayload = null
      try {
        const parsed = JSON.parse(coterRawText)
        if (parsed && typeof parsed === 'object') coterPayload = parsed
      } catch {
        coterPayload = null
      }
      if (!coterRes.ok) {
        const fromApi = coterPayload && typeof coterPayload.error === 'string' ? coterPayload.error.trim() : ''
        if (coterRes.status === 401) {
          throw new Error(
            fromApi ||
              'Session expirée ou non connecté — reconnectez-vous pour estimer le prix du véhicule.'
          )
        }
        throw new Error(fromApi || 'Impossible d’estimer le prix véhicule')
      }
      const coterData = coterPayload
      if (!coterData || typeof coterData !== 'object') {
        throw new Error('Réponse cotation invalide du serveur')
      }
      const vehiclePrice = Number(coterData?.cotation?.valeur_estimee)
      if (!Number.isFinite(vehiclePrice) || vehiclePrice <= 0) throw new Error('Prix véhicule indisponible')
      setEstimatedVehiclePrice(vehiclePrice)

      const residualPayload = {
        make: makeNorm || 'n/a',
        model: modelNorm || 'n/a',
        annee: anneeNum,
        km: kmNum,
        annualKm: annualKmNum,
        version,
        titre: titrePourMarcheLease,
        ...(autoOnlyAccount || autoPrecisionEnabled ? { vehicle_type: 'auto' } : {}),
        ...(autoPrecisionEnabled && String(motorisationChoisie || '').trim()
          ? { selected_motorisation: String(motorisationChoisie).trim() }
          : {}),
        ...(autoPrecisionEnabled && String(carrosserieChoisie || '').trim()
          ? { selected_carrosserie: String(carrosserieChoisie).trim() }
          : {}),
        ...(autoPrecisionEnabled && String(version || '').trim()
          ? { details_precision: String(version).trim().slice(0, 200) }
          : {}),
        garantie,
        had_accident: accident,
        fallback_depreciation_table: prefs?.leasing_depreciation_fallback_table || [],
      }
      const curveRes = await fetchWithTimeout(`${BACKEND}/api/leasing/residual-curve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...residualPayload, duration_months_list: DURATION_OPTIONS }),
      }, 90000)
      const curveText = await curveRes.text()
      const curveParsed = parseJsonBodySafe(curveText)
      let entries = []
      if (curveRes.ok && Array.isArray(curveParsed.data?.entries) && curveParsed.data.entries.length > 0) {
        entries = curveParsed.data.entries
      } else {
        const { depKm, depYear } = slopesFromPrefsFallback(
          modelNorm,
          vehiclePrice,
          prefs?.leasing_depreciation_fallback_table || []
        )
        const rawRows = []
        for (const d of DURATION_OPTIONS) {
          try {
            const rr = await fetchWithTimeout(`${BACKEND}/api/leasing/residual`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...residualPayload, durationMonths: d }),
            }, 45000)
            const txt = await rr.text()
            const p = parseJsonBodySafe(txt)
            if (p.ok && p.data && !p.data.error && Number.isFinite(Number(p.data.residualValue))) rawRows.push(p.data)
            else rawRows.push({ error: true })
          } catch {
            rawRows.push({ error: true })
          }
        }
        entries = applyCurveToResidualEntries(DURATION_OPTIONS, rawRows, {
          annualKm: annualKmNum,
          vehiclePrice,
          depKm,
          depYear,
          km: kmNum,
        })
      }
      const byDur = new Map(entries.map((e) => [Number(e.durationMonths), e]))
      const primaryResidual = byDur.get(durationNum)
      if (!primaryResidual || primaryResidual.error || !Number.isFinite(Number(primaryResidual.residualValue))) {
        throw new Error('Résiduelle non prédite — données marché insuffisantes')
      }

      const depositEff = Math.min(Number(downChf) || 0, vehiclePrice * 0.4)
      const primaryLease = calculateLeasing({
        vehiclePrice,
        downPayment: depositEff,
        durationMonths: durationNum,
        annualKm: annualKmNum,
        nominalRate: nominalRateNum,
        residualValue: Number(primaryResidual.residualValue),
        insuranceMonthly: 0,
      })
      setResidual(primaryResidual)
      setResult(primaryLease)
      setRiskIaMarketRvAnchor(Number(primaryResidual.residualValue))
      setRiskIaFbLow(
        Number.isFinite(Number(primaryResidual.fourchetteBasse)) ? Number(primaryResidual.fourchetteBasse) : null
      )
      setRiskIaFbHigh(
        Number.isFinite(Number(primaryResidual.fourchetteHaute)) ? Number(primaryResidual.fourchetteHaute) : null
      )
      setPriceSourceTag(stockLinkRef.current.vehicleKey ? 'stock' : 'cotation')

      const cols = []
      for (const d of DURATION_OPTIONS) {
        const rd = byDur.get(d)
        if (!rd || rd.error || !Number.isFinite(Number(rd?.residualValue))) {
          cols.push({ durationMonths: d, error: true })
          continue
        }
        try {
          const rdUse = { ...rd, residualValue: Number(rd.residualValue) }
          const leaseOut = calculateLeasing({
            vehiclePrice,
            downPayment: depositEff,
            durationMonths: d,
            annualKm: annualKmNum,
            nominalRate: nominalRateNum,
            residualValue: rdUse.residualValue,
            insuranceMonthly: 0,
          })
          cols.push({ durationMonths: d, residual: rdUse, lease: leaseOut })
        } catch {
          cols.push({ durationMonths: d, error: true })
        }
      }
      setComparisonCols(
        tuneLeaseComparisonColumns(cols, {
          vehiclePrice,
          downPayment: depositEff,
          nominalRate: nominalRateNum,
          annualKm: annualKmNum,
        })
      )

      const f = stockLinkRef.current.fingerprint
      const vehicleKeyLinked =
        f &&
        stockLinkRef.current.vehicleKey &&
        String(makeNorm) === f.make &&
        String(modelNorm) === f.model &&
        anneeNum === f.annee &&
        kmNum === f.km
          ? stockLinkRef.current.vehicleKey
          : null

      if (token) {
        fetchWithTimeout(`${BACKEND}/api/leasing/audit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            source: 'leasing',
            action: 'simulate',
            inputs: {
              vehiclePrice,
              downPayment: depositEff,
              durationMonths: durationNum,
              annualKm: annualKmNum,
              nominalRate: nominalRateNum,
              make: makeNorm,
              model: modelNorm,
              annee: anneeNum,
              km: kmNum,
            },
            outputs: { ...primaryLease, residualValue: Number(primaryResidual.residualValue) },
          }),
        }).catch(() => {})
      }
      appendLeasingHistory({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        at: new Date().toISOString(),
        vehicleKey: vehicleKeyLinked,
        make: makeNorm,
        model: modelNorm,
        annee: anneeNum,
        km: kmNum,
        version,
        motorisation: motorisationChoisie,
        carrosserie: carrosserieChoisie,
        garantie,
        accident,
        downPct: Number(downPct),
        downChf: depositEff,
        durationMonths: durationNum,
        annualKm: annualKmNum,
        nominalRate: nominalRateNum,
        vehiclePrice,
        monthly: primaryLease.monthlyPayment,
        total: primaryLease.totalLeasingCost,
        taeg: primaryLease.taeg,
        residualValue: Number(primaryResidual.residualValue),
      })
    } catch (e) {
    setResult(null)
    setResidual(null)
    setRiskAdjustedResult(null)
    setRiskIaMarketRvAnchor(null)
    setRiskIaFbLow(null)
    setRiskIaFbHigh(null)
    setComparisonCols([])
    setError(e?.message || 'Erreur de calcul')
    } finally {
      setLoading(false)
    }
  }

  const applyHistoryEntry = (h) => {
    leaseHydrateRef.current = 2
    setMake(String(h.make || '').toLowerCase())
    setModel(String(h.model || ''))
    setAnnee(String(h.annee ?? ''))
    setKm(String(h.km ?? ''))
    setVersion(String(h.version || ''))
    setMotorisationChoisie(String(h.motorisation || ''))
    setCarrosserieChoisie(String(h.carrosserie || ''))
    setGarantie(Boolean(h.garantie))
    setAccident(Boolean(h.accident))
    if (Number.isFinite(Number(h.downPct))) setDownPct(Number(h.downPct))
    if (Number.isFinite(Number(h.downChf))) setDownChf(Number(h.downChf))
    if ([24, 36, 48, 60].includes(Number(h.durationMonths))) setDurationMonths(Number(h.durationMonths))
    if (Number.isFinite(Number(h.annualKm)) && h.annualKm > 0) setAnnualKm(Number(h.annualKm))
    if (h.nominalRate != null && String(h.nominalRate) !== '') setNominalRate(String(h.nominalRate))
    setResult(null)
    setResidual(null)
    setRiskAdjustedResult(null)
    setRiskIaMarketRvAnchor(null)
    setRiskIaFbLow(null)
    setRiskIaFbHigh(null)
    setEstimatedVehiclePrice(null)
    setComparisonCols([])
    setError('')
    stockLinkRef.current = { vehicleKey: null, fingerprint: null }
    window.setTimeout(() => {
      leaseHydrateRef.current = 0
    }, 0)
  }

  const monthlyDisplay = leaseForDisplay ? leaseForDisplay.monthlyPayment : null

  const financialDetail = useMemo(() => {
    if (!leaseForDisplay || !residual || residual.error || !Number.isFinite(currentVehiclePrice) || currentVehiclePrice <= 0) return null
    const lease = leaseForDisplay
    const vp = currentVehiclePrice
    const dep = Math.min(Number(downChf) || 0, vp * 0.4)
    const rv = Number(residualValueForDisplay)
    if (!Number.isFinite(rv)) return null
    const financedCadre = Math.max(0, vp - dep)
    const sumInt = Number(lease.totalInterest)
    const months = Number(durationMonths)
    const totalMens = lease.monthlyPayment * months
    const avgInt = months > 0 ? sumInt / months : 0
    const residualPv = Number(lease.residualActualized)
    return {
      vp,
      dep,
      rv,
      residualPv: Number.isFinite(residualPv) ? residualPv : null,
      financed: financedCadre,
      sumInt,
      totalMens,
      months,
      avgInt,
      monthlyPaymentRow: lease.monthlyPayment,
    }
  }, [leaseForDisplay, residual, currentVehiclePrice, downChf, durationMonths, residualValueForDisplay])

  const riskAiRisksDeduped = useMemo(() => dedupeRisksDisplay(riskAiData?.risks || []), [riskAiData])

  useEffect(() => {
    if (!riskAiEnabled) {
      riskIaLastIdentityRef.current = null
      setRiskAiData(null)
      setRiskAiLoading(false)
      setRiskAdjustedResult(null)
      setRiskIaDetailsOpen(false)
      setRiskIaMarketRvAnchor(null)
      setRiskIaFbLow(null)
      setRiskIaFbHigh(null)
    }
  }, [riskAiEnabled])

  useEffect(() => {
    setRiskIaDetailsOpen(false)
  }, [riskAiData])

  useEffect(() => {
    if (!riskAiData || riskAiData.error || !result || !residual || !currentVehiclePrice) {
      if (!riskAiData || riskAiData.error) setRiskAdjustedResult(null)
      return
    }
    const baseRv = Number(residual.residualValue)
    const pct = Number(riskAiData.total_impact_pct)
    if (!Number.isFinite(baseRv) || baseRv <= 0 || !Number.isFinite(pct)) {
      setRiskAdjustedResult(null)
      return
    }
    const adjRv = baseRv * (1 + pct / 100)
    if (!Number.isFinite(adjRv) || adjRv <= 0) {
      setRiskAdjustedResult(null)
      return
    }
    try {
      const nominalRateNum = Number(nominalRate)
      const depositEff = Math.min(Number(downChf) || 0, currentVehiclePrice * 0.4)
      const adj = calculateLeasing({
        vehiclePrice: currentVehiclePrice,
        downPayment: depositEff,
        durationMonths: Number(durationMonths),
        annualKm: Number(annualKm),
        nominalRate: nominalRateNum,
        residualValue: adjRv,
        insuranceMonthly: 0,
      })
      setRiskAdjustedResult({
        ...adj,
        adjustedResidualValue: adjRv,
        baseResidualValue: Number(residual.residualValue),
        totalImpactPct: Number(riskAiData.total_impact_pct),
      })
    } catch {
      setRiskAdjustedResult(null)
    }
  }, [riskAiData, result, residual, currentVehiclePrice, nominalRate, downChf, durationMonths, annualKm])

  const recalcRiskIa = useCallback(() => {
    riskIaForceRef.current = true
    setRiskAiData(null)
    setRiskAdjustedResult(null)
    setRiskIaDetailsOpen(false)
    setRiskAiLoading(true)
    setRiskIaVersion((v) => v + 1)
  }, [])

  useEffect(() => {
    if (!riskAiEnabled || !token) return
    const rvRaw =
      riskIaMarketRvAnchor != null && Number.isFinite(riskIaMarketRvAnchor) && riskIaMarketRvAnchor > 0
        ? riskIaMarketRvAnchor
        : riskAiResidualRv
    if (rvRaw == null) {
      // Pas de VR de marché à analyser : on ne lance pas l’IA mais on signale
      // explicitement qu’on n’attend plus rien d’elle (sinon le placeholder
      // « Analyse IA en cours » resterait bloqué — voir runCalculation).
      riskIaLastIdentityRef.current = null
      setRiskAiLoading(false)
      setRiskAiData({
        error: true,
        risks: [],
        total_impact_pct: 0,
        confidence: 'low',
        summary_fr: '',
        adjustedResidualValue: null,
        cacheHit: false,
        skipped: true,
      })
      setRiskAdjustedResult(null)
      return
    }
    riskIaLastIdentityRef.current = riskIaIdentityKey
    setRiskAiData(null)
    setRiskAdjustedResult(null)
    setRiskIaDetailsOpen(false)
    let cancelled = false
    const force = riskIaForceRef.current
    riskIaForceRef.current = false
    setRiskAiLoading(true)

    const fbLo = Number.isFinite(riskAiFbLoForApi) ? riskAiFbLoForApi : undefined
    const fbHi = Number.isFinite(riskAiFbHiForApi) ? riskAiFbHiForApi : undefined
    const body = {
      make: String(make || '').trim().toLowerCase(),
      model: String(model || '').trim(),
      annee,
      version: String(version || '').trim(),
      motorisation: String(motorisationChoisie || '').trim(),
      carrosserie: String(carrosserieChoisie || '').trim(),
      canton: LEASING_RISK_CANTON_DEFAULT,
      currentResidualValue: rvRaw,
      ...(fbLo != null ? { fourchetteBasse: fbLo } : {}),
      ...(fbHi != null ? { fourchetteHaute: fbHi } : {}),
      force,
    }

    fetchWithTimeout(
      `${BACKEND}/api/leasing/risk-analysis`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
      120000
    )
      .then(async (r) => {
        const j = await r.json().catch(() => ({}))
        return { ok: r.ok, j }
      })
      .then(({ ok, j }) => {
        if (cancelled) return
        if (!ok) {
          setRiskAiData({
            error: true,
            risks: [],
            total_impact_pct: 0,
            confidence: 'low',
            summary_fr: '',
            adjustedResidualValue: rvRaw,
            cacheHit: false,
          })
          return
        }
        setRiskAiData(normalizeRiskAiPayload(j, rvRaw))
      })
      .catch(() => {
        if (cancelled) return
        setRiskAiData({
          error: true,
          risks: [],
          total_impact_pct: 0,
          confidence: 'low',
          summary_fr: '',
          adjustedResidualValue: rvRaw,
          cacheHit: false,
        })
      })
      .finally(() => {
        if (!cancelled) setRiskAiLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    riskAiEnabled,
    token,
    riskIaMarketRvAnchor,
    riskAiResidualRv,
    riskAiFbLoForApi,
    riskAiFbHiForApi,
    riskIaIdentityKey,
    make,
    model,
    annee,
    version,
    motorisationChoisie,
    carrosserieChoisie,
    riskIaVersion,
  ])

  const applyComparisonColumn = useCallback((col) => {
    if (!col || col.error || !col.lease || !col.residual) return
    setDurationMonths(col.durationMonths)
    setResidual(col.residual)
    setResult(col.lease)
  }, [])

  return (
    <div
      ref={leasingScrollRef}
      onScroll={onLeasingScroll}
      style={{ flex: 1, overflow: 'auto', padding: 'clamp(1rem, 2.2vw, 2rem)', width: '100%' }}
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', width: '100%', position: 'relative' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: 6 }}>
              Leasing
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 14, margin: 0, lineHeight: 1.45 }}>
              Paramétrez un véhicule et un contrat : le prix est estimé comme en cotation, la résiduelle sur le marché
              réel, puis la mensualité s’affiche sous le formulaire.
            </p>
          </div>

          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'rgba(226,232,240,0.5)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 12,
              }}
            >
              Véhicule
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: 'var(--muted)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  Marque
                </label>
                <select style={selectStyle} value={make} onChange={(e) => setMake(e.target.value)}>
                  <option value="" style={optionStyle}>
                    Sélectionner…
                  </option>
                  {marques
                    .filter((m) => String(m?.make || '').trim())
                    .map((m) => {
                      const mk = String(m.make).trim().toLowerCase()
                      const nb = Number(m.nb)
                      return (
                        <option key={mk} value={mk} style={optionStyle}>
                          {mk.charAt(0).toUpperCase() + mk.slice(1)}
                          {Number.isFinite(nb) ? ` (${nb})` : ''}
                        </option>
                      )
                    })}
                </select>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: 'var(--muted)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  Modèle
                </label>
                <select style={selectStyle} value={model} onChange={(e) => setModel(e.target.value)} disabled={!make}>
                  <option value="" style={optionStyle}>
                    Sélectionner…
                  </option>
                  {modeles.map((m) => (
                    <option key={m.model} value={m.model} style={optionStyle}>
                      {String(m.model).toUpperCase()} ({m.nb})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: 'var(--muted)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  Année
                </label>
                <select style={selectStyle} value={annee} onChange={(e) => setAnnee(e.target.value)} disabled={!model}>
                  <option value="" style={optionStyle}>
                    Sélectionner…
                  </option>
                  {annees.map((a) => (
                    <option key={a.annee} value={a.annee} style={optionStyle}>
                      {a.annee} ({a.nb})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Kilométrage
              </label>
              <input
                style={{ ...selectStyle, cursor: 'text' }}
                type="number"
                placeholder="ex: 15000"
                min={0}
                value={km}
                onChange={(e) => setKm(e.target.value)}
              />
            </div>

            {autoPrecisionEnabled ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                <div>
                  <label
                    style={{
                      fontSize: 11,
                      color: 'var(--muted)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    Motorisation (base)
                  </label>
                  <select
                    style={selectStyle}
                    value={motorisationChoisie}
                    onChange={(e) => setMotorisationChoisie(e.target.value)}
                    disabled={!model || !annee}
                  >
                    <option value="" style={{ background: '#1a1a1a', color: '#fff' }}>Non spécifiée (toutes)</option>
                    {cotationMotorisations.map((m) => (
                      <option key={m.value} value={m.value} style={{ background: '#1a1a1a' }}>
                        {m.value} ({m.nb})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11,
                      color: 'var(--muted)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    Carrosserie
                  </label>
                  <select
                    style={selectStyle}
                    value={carrosserieChoisie}
                    onChange={(e) => setCarrosserieChoisie(e.target.value)}
                    disabled={!model}
                  >
                    <option value="" style={{ background: '#1a1a1a', color: '#fff' }}>Aucune</option>
                    {cotationCarrosseries.map((v) => (
                      <option key={v.value} value={v.value} style={{ background: '#1a1a1a' }}>
                        {formatCarrosserieLabel(v.value)} ({v.nb})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Finition (optionnel)
              </label>
              <input
                style={{ ...selectStyle, cursor: 'text' }}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="SP, Tracer, DCT…"
              />
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              {[
                { label: 'Avec garantie', val: garantie, set: setGarantie },
                { label: 'Accident déclaré', val: accident, set: setAccident },
              ].map((opt, i) => (
                <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--muted)' }}>
                  <input
                    type="checkbox"
                    checked={opt.val}
                    onChange={(e) => opt.set(e.target.checked)}
                    style={{ accentColor: '#001E50', width: 16, height: 16 }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'rgba(226,232,240,0.5)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 12,
                marginTop: 4,
              }}
            >
              Paramètres leasing
            </div>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                fontSize: 13,
                color: 'var(--muted)',
                marginBottom: 14,
              }}
            >
              <input
                type="checkbox"
                checked={riskAiEnabled}
                onChange={(e) => setRiskAiEnabled(e.target.checked)}
                style={{ accentColor: '#001E50', width: 16, height: 16 }}
              />
              Inclure l’analyse IA de risque (décote)
            </label>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Acompte (CHF) · {downPct.toFixed(1)}%
              </label>
              <input
                style={{ ...selectStyle, cursor: 'text' }}
                type="number"
                min={0}
                value={Math.round(downChf)}
                onChange={(e) => handleDownChfInput(e.target.value)}
              />
              <input
                style={{ width: '100%', marginTop: 8, accentColor: '#3b82f6' }}
                type="range"
                min={0}
                max={40}
                step={0.5}
                value={downPct}
                onChange={(e) => setDownPct(Number(e.target.value))}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Durée
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {DURATION_OPTIONS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDurationMonths(m)}
                    style={{
                      border: `1px solid ${durationMonths === m ? 'rgba(148,163,184,0.5)' : 'var(--border)'}`,
                      borderRadius: 6,
                      padding: '7px 12px',
                      fontSize: 12,
                      color: durationMonths === m ? '#f1f5f9' : 'var(--text-secondary)',
                      background: durationMonths === m ? 'rgba(255,255,255,0.08)' : 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {m} mois
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Km annuels
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {KM_OPTIONS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAnnualKm(v)}
                    style={{
                      border: `1px solid ${annualKm === v ? 'rgba(148,163,184,0.5)' : 'var(--border)'}`,
                      borderRadius: 6,
                      padding: '7px 12px',
                      fontSize: 12,
                      color: annualKm === v ? '#f1f5f9' : 'var(--text-secondary)',
                      background: annualKm === v ? 'rgba(255,255,255,0.08)' : 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {v / 1000}k
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <input
                  type="number"
                  min={1000}
                  max={200000}
                  step={100}
                  value={Math.round(Number(annualKm) || 0)}
                  onChange={(e) => {
                    const n = Number(e.target.value)
                    if (Number.isFinite(n) && n >= 1000 && n <= 200000) setAnnualKm(Math.round(n))
                  }}
                  style={{
                    width: '100%',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    background: 'var(--surface-alt)',
                    color: 'var(--text)',
                    padding: '9px 10px',
                    fontSize: 12,
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-tertiary)' }}>
                  Saisie libre: de 1 000 à 200 000 km/an.
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Taux nominal (%)
              </label>
              <input
                style={{ ...selectStyle, cursor: 'text', maxWidth: 200 }}
                type="number"
                min={0}
                step={0.1}
                value={nominalRate}
                onChange={(e) => setNominalRate(e.target.value)}
              />
              {rateOverLcc ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>Dépasse le plafond LCC ({lccLimit}%)</div>
              ) : null}
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: '8px 0 0', lineHeight: 1.45 }}>
                Prérempli avec le « Taux nominal par défaut » des réglages concession (compte / préférences), sinon 4,9&nbsp;% — modifiable ici uniquement pour la simulation.
              </p>
            </div>

            {error ? <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div> : null}

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              {(() => {
                // Le bouton reste verrouillé tant que le calcul leasing OU l’analyse
                // IA ne sont pas terminés — sinon on pourrait relancer un calcul
                // alors que l’IA précédente n’a pas encore répondu.
                const isBusy = loading || (riskAiEnabled && riskAiLoading)
                const isDisabled = isBusy || !make || !model || !annee || !km
                const label = loading
                  ? 'Calcul en cours…'
                  : isBusy
                    ? 'Analyse IA en cours…'
                    : 'Calculer le leasing'
                return (
                  <button
                    type="button"
                    onClick={() => runCalculation()}
                    disabled={isDisabled}
                    style={{
                      width: 'auto',
                      maxWidth: 280,
                      padding: '0.75rem 1.35rem',
                      borderRadius: 6,
                      background: isDisabled
                        ? 'rgba(0,30,80,0.3)'
                        : 'linear-gradient(135deg, #001E50, #1e3a8a)',
                      border: 'none',
                      color: '#fff',
                      fontFamily: 'inherit',
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: isBusy ? 'wait' : 'pointer',
                    }}
                  >
                    {label}
                  </button>
                )
              })()}
            </div>
          </div>

          {/*
            Tant que l’analyse risque IA n’a pas répondu, on n’affiche pas le bloc
            résultat : utilisateur préfère attendre quelques secondes plutôt que voir
            un mensuel/résiduel qui change ensuite. Si l’IA est désactivée ou a déjà
            répondu (success/error), riskAiData est non-null → on affiche normalement.
          */}
          {result && residual && !residual.error && riskAiEnabled && riskAiData == null ? (
            <div
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: '1.5rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
              role="status"
              aria-live="polite"
            >
              <RefreshCw
                size={24}
                style={{ animation: 'riskIaSpin 0.85s linear infinite', color: '#fde68a', flexShrink: 0 }}
                aria-hidden
              />
              <style>{`@keyframes riskIaSpin { to { transform: rotate(360deg); } }`}</style>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fde68a' }}>
                  Analyse risque IA en cours…
                </div>
                <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.7)', marginTop: 4, lineHeight: 1.5 }}>
                  Recherche web, agrégation des signaux marché et ajustement de la valeur résiduelle.
                  Le résultat complet du leasing s’affichera dès la fin de l’analyse (10–30 s).
                </div>
              </div>
            </div>
          ) : null}

          {result && residual && !residual.error && (!riskAiEnabled || riskAiData != null) ? (
            <div
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: '1.5rem',
                marginBottom: '1.5rem',
              }}
            >
              {/* 1 — En-tête résultat */}
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#bfdbfe',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {String(make || '').charAt(0).toUpperCase()}
                {String(make || '').slice(1)} {String(model || '').toUpperCase()} · {annee} ·{' '}
                {Number(km || 0).toLocaleString('fr-CH')} km
              </div>
              {(() => {
                const chips = [
                  String(version || '').trim() ? `Finition: ${String(version).trim()}` : null,
                  String(motorisationChoisie || '').trim() ? `Motorisation: ${String(motorisationChoisie).trim()}` : null,
                  String(carrosserieChoisie || '').trim()
                    ? `Carrosserie: ${formatCarrosserieLabel(String(carrosserieChoisie).trim())}`
                    : null,
                ].filter(Boolean)
                if (chips.length === 0) return null
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {chips.map((txt) => (
                      <span
                        key={txt}
                        style={{
                          fontSize: 11,
                          color: 'rgba(226,232,240,0.78)',
                          border: '1px solid rgba(148,163,184,0.28)',
                          background: 'rgba(148,163,184,0.08)',
                          borderRadius: 999,
                          padding: '3px 8px',
                        }}
                      >
                        {txt}
                      </span>
                    ))}
                  </div>
                )
              })()}

              <div
                style={{
                  padding: '1.2rem 1.5rem',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 14,
                  marginBottom: 16,
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 14, rowGap: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#bfdbfe',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    Mensualité
                    {displayUsesRiskAdjusted ? (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: '#fde68a',
                          border: '1px solid rgba(251,191,36,0.45)',
                          borderRadius: 999,
                          padding: '2px 8px',
                        }}
                      >
                        VR risque IA
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 38, fontWeight: 300, color: '#fff', lineHeight: 1 }}>{formatChf(monthlyDisplay)}</div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: '1px solid rgba(148,163,184,0.35)',
                      color: '#cbd5e1',
                    }}
                  >
                    TAEG {formatTaegPct(leaseForDisplay?.taeg)}
                  </div>
                </div>
                {financialDetail && financialDetail.months > 0 ? (
                  <div style={{ fontSize: 13, color: 'rgba(226,232,240,0.72)', marginTop: 10 }}>
                    dont environ{' '}
                    <span style={{ fontWeight: 700, color: '#e2e8f0' }}>
                      {formatChf(financialDetail.avgInt)}
                    </span>{' '}
                    d’intérêts / mois en moyenne
                  </div>
                ) : null}
                <div style={{ fontSize: 13, color: 'rgba(226,232,240,0.72)', marginTop: 8 }}>
                  Acompte :{' '}
                  <span style={{ fontWeight: 700, color: '#e2e8f0' }}>
                    {formatChf(financialDetail?.dep ?? Math.min(Number(downChf) || 0, currentVehiclePrice * 0.4))}
                  </span>
                  {financialDetail && financialDetail.vp > 0 ? (
                    <span> ({((financialDetail.dep / financialDetail.vp) * 100).toFixed(1)} %)</span>
                  ) : null}
                </div>
                <div style={{ fontSize: 13, color: 'rgba(226,232,240,0.72)', marginTop: 8 }}>
                  Prix véhicule (estimation){' '}
                  <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{formatChf(currentVehiclePrice)}</span>{' '}
                  <span style={{ color: 'rgba(226,232,240,0.45)' }}>
                    {priceSourceTag === 'stock' ? '(prix stock — fiche liée)' : '(cotation moteur)'}
                  </span>
                </div>
              </div>

              {/* 2 — Détail financier */}
              {financialDetail ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '1rem 1.25rem',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.08)',
                    background: 'rgba(0,30,80,0.12)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'rgba(226,232,240,0.55)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: 10,
                    }}
                  >
                    Détail financier
                  </div>
                  {(() => {
                    const pctDep = financialDetail.vp > 0 ? ((financialDetail.dep / financialDetail.vp) * 100).toFixed(1) : '0'
                    const rows = [
                      { pre: '', lab: 'Prix véhicule', val: financialDetail.vp, br: true },
                      { pre: '− ', lab: 'Acompte', sub: `(${pctDep} % du prix)`, val: -financialDetail.dep, br: true },
                      { pre: '= ', lab: 'Capital financé (hors rachat résiduel)', val: financialDetail.financed, strong: true, br: true },
                      {
                        pre: '',
                        lab: 'Résiduelle de rachat (fin de bail)',
                        sub: 'non déduite du prix — payable en fin de contrat',
                        val: financialDetail.rv,
                        br: true,
                        absOnly: true,
                      },
                      ...(financialDetail.residualPv != null && Number.isFinite(financialDetail.residualPv)
                        ? [
                            {
                              pre: '',
                              lab: 'Résiduelle (valeur présente)',
                              sub: 'actualisée au taux nominal — base du calcul de la mensualité',
                              val: financialDetail.residualPv,
                              br: true,
                              absOnly: true,
                              muted: true,
                            },
                          ]
                        : []),
                      { pre: '+ ', lab: 'Intérêts totaux', val: financialDetail.sumInt, br: true },
                      {
                        pre: '= ',
                        lab: 'Total des mensualités',
                        sub: `${financialDetail.months} × ${formatChf(financialDetail.monthlyPaymentRow)}`,
                        val: financialDetail.totalMens,
                        strong: true,
                        br: false,
                      },
                    ]
                    return rows.map((row) => (
                      <div
                        key={row.lab}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          gap: 12,
                          padding: '7px 0',
                          borderBottom: row.br ? '1px solid rgba(255,255,255,0.06)' : 'none',
                          fontSize: row.muted ? 12 : row.strong ? 14 : 13,
                          fontWeight: row.strong ? 700 : row.muted ? 500 : 400,
                          color: row.muted ? 'rgba(226,232,240,0.62)' : row.strong ? '#f1f5f9' : 'rgba(226,232,240,0.88)',
                        }}
                      >
                        <span>
                          {row.pre}
                          {row.lab}
                          {row.sub ? (
                            <span style={{ fontWeight: 400, color: 'rgba(226,232,240,0.45)', fontSize: 11 }}> {row.sub}</span>
                          ) : null}
                        </span>
                        <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {row.absOnly ? '' : row.val < 0 ? '−' : ''}
                          {formatChf(row.absOnly ? row.val : Math.abs(row.val))}
                        </span>
                      </div>
                    ))
                  })()}
                </div>
              ) : null}

              {/* 3 — Valeur résiduelle */}
              <div
                style={{
                  marginBottom: 16,
                  padding: '1.1rem 1.25rem',
                  borderRadius: 12,
                  border: '1px solid var(--border)',
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'rgba(226,232,240,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: 8,
                  }}
                >
                  Valeur résiduelle (fin de contrat)
                </div>
                {riskAiEnabled && riskAiData && !riskAiData.error ? (
                  <>
                    <div
                      style={{
                        fontSize: 32,
                        fontWeight: 600,
                        color: '#fde68a',
                        fontVariantNumeric: 'tabular-nums',
                        opacity: riskAiLoading ? 0.88 : 1,
                      }}
                    >
                      {riskIaAdjustedResidualChf != null ? formatChf(riskIaAdjustedResidualChf) : formatChf(Number(riskAiData.adjustedResidualValue))}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.45 }}>
                      Estimation marché (hors ajustement risque) :{' '}
                      <span style={{ fontWeight: 600, color: '#e2e8f0' }}>{formatChf(residual.residualValue)}</span>
                      {' · '}
                      <span style={{ fontWeight: 700, color: '#fca5a5' }}>
                        Ajustement net cumulé {Number(riskAiData.total_impact_pct) <= 0 ? '' : '+'}
                        {Number(riskAiData.total_impact_pct).toFixed(1)} %
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 32, fontWeight: 300, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                    {formatChf(residual.residualValue)}
                  </div>
                )}
                <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5 }}>
                  {residualMethodText(
                    residual,
                    [model, String(version || '').trim()].filter(Boolean).join(' ').trim() || model
                  )}
                </p>
                <div
                  style={{
                    marginTop: 10,
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.02)',
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    Détail du calcul résiduel
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                    Méthode: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{residualMethodLabel(residual.method)}</span>
                    <br />
                    Modèle de base: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{String(residual.modeleReference || model || '').toUpperCase()}</span>
                    <br />
                    Projection: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{Number.isFinite(Number(residual.projectedYear)) ? residual.projectedYear : '—'}</span>
                    {' · '}
                    <span style={{ color: '#e2e8f0', fontWeight: 600 }}>
                      {Number.isFinite(Number(residual.projectedKm)) ? `${Math.round(Number(residual.projectedKm)).toLocaleString('fr-CH')} km` : '—'}
                    </span>
                    <br />
                    Données utilisées: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{Number(residual.marketSampleSize || 0)}</span> annonces
                    {' · '}
                    <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{Number(residual.realSalesSampleSize || 0)}</span> ventes réelles
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {String(version || '').trim() ? (
                      <span style={{ fontSize: 11, border: '1px solid var(--border)', borderRadius: 999, padding: '3px 8px', color: 'var(--text-secondary)' }}>
                        Finition: {String(version).trim()}
                      </span>
                    ) : null}
                    {String(motorisationChoisie || '').trim() ? (
                      <span style={{ fontSize: 11, border: '1px solid var(--border)', borderRadius: 999, padding: '3px 8px', color: 'var(--text-secondary)' }}>
                        Motorisation: {String(motorisationChoisie).trim()}
                      </span>
                    ) : null}
                    {String(carrosserieChoisie || '').trim() ? (
                      <span style={{ fontSize: 11, border: '1px solid var(--border)', borderRadius: 999, padding: '3px 8px', color: 'var(--text-secondary)' }}>
                        Carrosserie: {formatCarrosserieLabel(String(carrosserieChoisie).trim())}
                      </span>
                    ) : null}
                    <span style={{ fontSize: 11, border: '1px solid var(--border)', borderRadius: 999, padding: '3px 8px', color: 'var(--text-secondary)' }}>
                      Usage: {Math.round(Number(annualKm) || 0).toLocaleString('fr-CH')} km/an
                    </span>
                    <span style={{ fontSize: 11, border: '1px solid var(--border)', borderRadius: 999, padding: '3px 8px', color: 'var(--text-secondary)' }}>
                      Durée: {durationMonths} mois
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Confiance</span>
                  <span
                    style={{
                      borderRadius: 999,
                      border: '1px solid var(--border)',
                      padding: '3px 10px',
                      fontSize: 11,
                      color: confidenceFr(residual).color,
                    }}
                    title={confidenceFr(residual).tip}
                  >
                    {confidenceFr(residual).txt}
                  </span>
                  {Number.isFinite(Number(residual.liquidityScore)) ? (
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      Liquidité{' '}
                      <span style={{ fontWeight: 700, color: '#e2e8f0' }}>
                        {Number(residual.liquidityScore).toLocaleString('fr-CH', { maximumFractionDigits: 1 })} / 10
                      </span>
                      {liquidityLabelFr(residual.liquidityLabel) ? (
                        <span style={{ color: 'rgba(226,232,240,0.55)' }}> — {liquidityLabelFr(residual.liquidityLabel)}</span>
                      ) : null}
                    </span>
                  ) : null}
                </div>
                {Number.isFinite(Number(residual.joursMoyenVente)) && Number(residual.joursMoyenVente) > 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                    Délai de vente moyen observé (marché) :{' '}
                    <span style={{ fontWeight: 600, color: '#e2e8f0' }}>
                      {Math.round(Number(residual.joursMoyenVente))} jours
                    </span>
                  </div>
                ) : null}
                {Number.isFinite(Number(residual.fourchetteBasse)) && Number.isFinite(Number(residual.fourchetteHaute)) ? (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                    Fourchette indicative : entre {formatChf(residual.fourchetteBasse)} et {formatChf(residual.fourchetteHaute)}
                  </div>
                ) : null}

                {riskAiEnabled && !token ? (
                  <p style={{ fontSize: 12, color: 'rgba(148,163,184,0.88)', marginTop: 12, lineHeight: 1.45 }}>
                    Connectez-vous pour activer l&apos;analyse IA de risque (ajustement de la valeur résiduelle).
                  </p>
                ) : null}

                {riskAiEnabled && !riskAiLoading && riskAiData?.error ? (
                  <p style={{ fontSize: 12, color: 'rgba(248,113,113,0.82)', marginTop: 14, lineHeight: 1.5 }}>
                    Analyse de risque temporairement indisponible (service IA ou réseau).
                  </p>
                ) : null}

                {riskAiEnabled && riskAiData && !riskAiData.error ? (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 14,
                      borderTop: '1px solid rgba(251,191,36,0.2)',
                      opacity: riskAiLoading ? 0.88 : 1,
                    }}
                  >
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(253,230,138,0.65)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                      Risque de décote accélérée (IA)
                    </div>
                    {riskAiData.summary_fr ? (
                      <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, lineHeight: 1.5 }}>{riskAiData.summary_fr}</p>
                    ) : null}
                    {riskAiData.guard_applied && riskAiData.guard_note ? (
                      <p
                        style={{
                          fontSize: 11,
                          fontStyle: 'italic',
                          color: 'rgba(148,163,184,0.85)',
                          marginTop: 8,
                          lineHeight: 1.45,
                        }}
                      >
                        {riskAiData.guard_note}
                      </p>
                    ) : null}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 10 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Confiance (IA)</span>
                      {(() => {
                        const cf = confidenceRiskIaFr(riskAiData.confidence)
                        return (
                          <span
                            style={{
                              borderRadius: 999,
                              border: '1px solid var(--border)',
                              padding: '3px 10px',
                              fontSize: 11,
                              color: cf.color,
                            }}
                            title={cf.tip}
                          >
                            {cf.txt}
                          </span>
                        )
                      })()}
                      {riskAiData.cacheHit ? (
                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Cache</span>
                      ) : null}
                    </div>
                    {riskAiRisksDeduped.length > 0 ? (
                      <div style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={() => setRiskIaDetailsOpen((o) => !o)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 12,
                            color: '#bfdbfe',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontFamily: 'inherit',
                          }}
                        >
                          {riskIaDetailsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          Détails ({riskAiRisksDeduped.length} facteur{riskAiRisksDeduped.length > 1 ? 's' : ''})
                        </button>
                        {riskIaDetailsOpen ? (
                          <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
                            {riskAiRisksDeduped.map((r, idx) => (
                              <li
                                key={`${idx}-${riskIaEventDedupeKey(r)}`}
                                style={{
                                  borderTop: idx ? '1px solid rgba(255,255,255,0.06)' : 'none',
                                  padding: '12px 0',
                                }}
                              >
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                  <div style={{ marginTop: 2, color: '#94a3b8' }}>{riskIaCategoryIcon(r.category)}</div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                      <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>{r.event}</span>
                                      {probabilityChipIa(r.probability)}
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          padding: '2px 8px',
                                          borderRadius: 999,
                                          border: '1px solid var(--border)',
                                          color: 'var(--text-secondary)',
                                        }}
                                      >
                                        {r.horizon_months} mois
                                      </span>
                                      <span style={{ fontSize: 13, fontWeight: 800, color: '#fca5a5' }}>
                                        {Number(r.impact_pct).toFixed(1)} %
                                      </span>
                                    </div>
                                    {r.reasoning ? (
                                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.5 }}>{r.reasoning}</div>
                                    ) : null}
                                    {Array.isArray(r.sources) && r.sources.length > 0 ? (
                                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {r.sources.map((s, i) => (
                                          <a
                                            key={i}
                                            href={s.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ fontSize: 11, color: '#93c5fd', textDecoration: 'underline', wordBreak: 'break-all' }}
                                          >
                                            {s.title || s.url}
                                          </a>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                    <div style={{ marginTop: 14 }}>
                      <button
                        type="button"
                        disabled={riskAiLoading}
                        onClick={recalcRiskIa}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '8px 14px',
                          borderRadius: 8,
                          border: '1px solid var(--border)',
                          background: riskAiLoading ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.06)',
                          color: '#e2e8f0',
                          cursor: riskAiLoading ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <RefreshCw size={14} style={riskAiLoading ? { animation: 'riskIaSpin 0.9s linear infinite' } : undefined} aria-hidden />
                        Recalculer l’analyse IA
                      </button>
                      <style>{`@keyframes riskIaSpin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Comparateur durées */}
              {comparisonCols.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'rgba(226,232,240,0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: 10,
                    }}
                  >
                    Comparer les durées
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: 'rgba(148,163,184,0.72)',
                      lineHeight: 1.45,
                      marginBottom: 8,
                    }}
                  >
                    Icône triangle à côté d’une durée : signal discret (données marché ou profil de mensualité atypique) — survolez-la pour le détail.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                    {comparisonCols.map((col) => {
                      const iaOverlay = comparisonLeaseIaOverlay?.get(col.durationMonths)
                      const leaseShow = iaOverlay?.lease ?? col.lease
                      const residShow =
                        iaOverlay != null && Number.isFinite(Number(iaOverlay.residualValue))
                          ? Number(iaOverlay.residualValue)
                          : col.residual?.residualValue
                      const tuningFlags = [
                        ...(Array.isArray(col.residual?.tuningFlags) ? col.residual.tuningFlags : []),
                        ...(Array.isArray(iaOverlay?.tuningFlags) ? iaOverlay.tuningFlags : []),
                      ]
                      const hasVrAnomaly = tuningFlags.includes('vr_anomalie')
                      const hasMensAnomaly = tuningFlags.includes('mensualite_anomalie')
                      const compareWarningTitle = hasVrAnomaly
                        ? 'Données marché atypiques sur cette durée'
                        : hasMensAnomaly
                          ? 'Profil de mensualité atypique — vérifier la résiduelle marché de cette durée'
                          : ''
                      const active = col.durationMonths === durationMonths && !col.error
                      return (
                        <button
                          key={col.durationMonths}
                          type="button"
                          onClick={() => applyComparisonColumn(col)}
                          disabled={col.error}
                          style={{
                            textAlign: 'left',
                            padding: '10px 10px',
                            borderRadius: 10,
                            border: active ? '1px solid rgba(148,163,184,0.55)' : '1px solid var(--border)',
                            background: active ? 'rgba(0,30,80,0.35)' : 'rgba(255,255,255,0.03)',
                            cursor: col.error ? 'not-allowed' : 'pointer',
                            opacity: col.error ? 0.45 : 1,
                            color: 'inherit',
                            fontFamily: 'inherit',
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 800, color: '#bfdbfe', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span>{col.durationMonths} mois</span>
                            {compareWarningTitle ? (
                              <AlertTriangle
                                size={12}
                                strokeWidth={2.2}
                                title={compareWarningTitle}
                                style={{ color: 'rgba(245, 158, 11, 0.75)', flexShrink: 0 }}
                                aria-label={compareWarningTitle}
                              />
                            ) : null}
                          </div>
                          {col.error ? (
                            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>Indisponible</div>
                          ) : (
                            <>
                              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6 }}>{formatChf(leaseShow.monthlyPayment)}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>Coût total {formatChf(leaseShow.totalLeasingCost)}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Résid. {formatChf(residShow)}</div>
                              <div style={{ fontSize: 10, color: 'rgba(226,232,240,0.5)', marginTop: 4 }}>TAEG {formatTaegPct(leaseShow.taeg)}</div>
                            </>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              {/* Graphique */}
              <div
                style={{
                  height: 280,
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.01)',
                  padding: 8,
                  marginBottom: 16,
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                    <XAxis dataKey="month" stroke="rgba(226,232,240,0.52)" tick={{ fill: 'rgba(226,232,240,0.6)', fontSize: 11 }} />
                    <YAxis
                      yAxisId="left"
                      domain={[0, 'auto']}
                      stroke="rgba(226,232,240,0.52)"
                      tick={{ fill: 'rgba(226,232,240,0.6)', fontSize: 10 }}
                      width={52}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 'auto']}
                      stroke="rgba(226,232,240,0.52)"
                      tick={{ fill: 'rgba(226,232,240,0.6)', fontSize: 10 }}
                      width={44}
                    />
                    <Tooltip formatter={(v) => formatChf(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <ReferenceLine
                      yAxisId="left"
                      y={Number.isFinite(chartResidualReferenceY) ? chartResidualReferenceY : residualValueForDisplay}
                      stroke="rgba(226,232,240,0.45)"
                      strokeDasharray="5 5"
                      label={{ value: 'Résiduelle', position: 'insideTopRight', fill: 'rgba(226,232,240,0.55)', fontSize: 10 }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="remaining"
                      name="Capital restant"
                      stroke="#93c5fd"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cumulInterets"
                      name="Cumul intérêts"
                      stroke="#fca5a5"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.65)', marginTop: 6, lineHeight: 1.4 }}>
                  Courbe bleue : capital restant dû mois par mois (diffère de la résiduelle de rachat avant le dernier mois). La ligne
                  pointillée aligne la fin de la courbe sur la résiduelle du contrat affichée.
                </div>
              </div>

              {/* 4 — Pour le concessionnaire */}
              <div
                style={{
                  marginBottom: 16,
                  padding: '1rem 1.2rem',
                  borderRadius: 10,
                  border: '1px dashed rgba(148,163,184,0.35)',
                  background: 'rgba(0,0,0,0.15)',
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(226,232,240,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Pour le concessionnaire (non repris sur le lien client)
                </div>
                {Number.isFinite(Number(residual.fourchetteHaute)) &&
                Number.isFinite(Number(residual.residualValue)) &&
                Number(residual.fourchetteHaute) > Number(residual.residualValue) ? (
                  <p style={{ fontSize: 12, color: 'rgba(226,232,240,0.75)', lineHeight: 1.5, margin: '0 0 8px' }}>
                    Si le client ne rachète pas en fin de bail, revente haute de fourchette estimée à{' '}
                    <strong>{formatChf(residual.fourchetteHaute)}</strong> : écart indicatif vs résiduelle contractuelle{' '}
                    <strong>
                      +{formatChf(Number(residual.fourchetteHaute) - Number(residual.residualValue))}
                    </strong>
                    .
                  </p>
                ) : (
                  <p style={{ fontSize: 12, color: 'rgba(226,232,240,0.5)', margin: '0 0 8px' }}>
                    Fourchette haute non disponible — marge de revente non estimée ici.
                  </p>
                )}
                {confidenceFr(residual).txt === 'Faible' ? (
                  <p style={{ fontSize: 12, color: '#fca5a5', margin: '0 0 8px', lineHeight: 1.45 }}>
                    Données limitées sur ce modèle — la résiduelle est moins fiable pour argumenter auprès du client.
                  </p>
                ) : null}
                {Number(prefs?.leasing_commission_pct) > 0 && financialDetail && financialDetail.sumInt > 0 ? (
                  <p style={{ fontSize: 12, color: 'rgba(226,232,240,0.75)', margin: 0, lineHeight: 1.5 }}>
                    Commission estimée (réglages, {Number(prefs.leasing_commission_pct).toLocaleString('fr-CH')}% des intérêts simulés) :{' '}
                    <strong>
                      {formatChf((financialDetail.sumInt * Number(prefs.leasing_commission_pct)) / 100)}
                    </strong>
                    .
                  </p>
                ) : null}
              </div>

              <p style={{ marginTop: 4, fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
                Proposition indicative. L’octroi d’un leasing est soumis à examen de solvabilité selon la LCC. Plafond légal : {lccLimit}% —{' '}
                <a href="https://www.fedlex.admin.ch/eli/cc/2002/443/fr" target="_blank" rel="noreferrer" style={{ color: '#9db6dd' }}>
                  Ordonnance
                </a>
              </p>
            </div>
          ) : null}
        </div>

        <aside
          aria-label="Historique des simulations leasing"
          style={{
            width: isMobileLayout ? (leasingHistoryOpen ? 'min(272px, calc(100% - 16px))' : 40) : leasingHistoryOpen ? 272 : 44,
            flexShrink: 0,
            transition: isMobileLayout ? 'none' : 'width 200ms ease',
            borderLeft: isMobileLayout ? 'none' : '1px solid rgba(255,255,255,0.08)',
            paddingLeft: isMobileLayout ? 0 : leasingHistoryOpen ? 12 : 6,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            minHeight: isMobileLayout ? 0 : 120,
            position: isMobileLayout ? 'absolute' : 'relative',
            top: isMobileLayout ? 4 : 'auto',
            left: isMobileLayout ? 4 : 'auto',
            zIndex: isMobileLayout ? 8 : 'auto',
            background: isMobileLayout && leasingHistoryOpen ? 'rgba(21,25,32,0.96)' : 'transparent',
            border: isMobileLayout && leasingHistoryOpen ? '1px solid rgba(255,255,255,0.12)' : 'none',
            borderRadius: isMobileLayout ? 8 : 0,
            boxShadow: isMobileLayout && leasingHistoryOpen ? '0 8px 24px rgba(0,0,0,0.35)' : 'none',
          }}
        >
          <button
            type="button"
            onClick={() => setLeasingHistoryOpen((o) => !o)}
            aria-expanded={leasingHistoryOpen}
            title={leasingHistoryOpen ? 'Replier l’historique' : 'Afficher l’historique'}
            style={{
              alignSelf: 'flex-start',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(15,23,42,0.86)',
              color: '#e2e8f0',
              borderRadius: 4,
              padding: 6,
              cursor: 'pointer',
              marginBottom: 8,
            }}
          >
            {leasingHistoryOpen ? <ChevronRight size={18} aria-hidden /> : <ChevronLeft size={18} aria-hidden />}
          </button>
          {leasingHistoryOpen ? (
            <>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'rgba(226,232,240,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 10,
                }}
              >
                Historique ({LEASING_HISTORY_MAX} max.)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1, paddingRight: 4 }}>
                {leasingHistory.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0, lineHeight: 1.45 }}>
                    Aucune simulation enregistrée localement.
                  </p>
                ) : (
                  leasingHistory.map((h, idx) => (
                    <button
                      key={h.id || `${h.at}-${idx}`}
                      type="button"
                      onClick={() => applyHistoryEntry(h)}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        textAlign: 'left',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.03)',
                        padding: '8px 10px',
                        cursor: 'pointer',
                        color: 'inherit',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 4,
                          flexShrink: 0,
                          background: 'rgba(0,30,80,0.35)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#bfdbfe',
                        }}
                      >
                        {h.durationMonths}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#f1f5f9',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {String(h.make || '').charAt(0).toUpperCase()}
                          {String(h.make || '').slice(1)} {String(h.model || '').toUpperCase()}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#e2e8f0', marginTop: 2 }}>
                          {Number(h.monthly).toLocaleString('fr-CH')} CHF<span style={{ fontWeight: 600, fontSize: 11 }}>/mois</span>
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(226,232,240,0.38)', marginTop: 2 }}>
                          {h.at ? new Date(h.at).toLocaleString('fr-CH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : null}
        </aside>
      </div>
      {showBackTop ? (
        <button
          type="button"
          className="back-top-btn"
          onClick={scrollLeasingTop}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 40,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'rgba(23,26,31,0.94)',
            color: '#e2e8f0',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Remonter
        </button>
      ) : null}
    </div>
  )
}
